import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';
import { getOrderListDB, updateOrderListDB } from 'slice/orderSliceDB';
import { useNavigate } from 'react-router-dom';
import { TbShoppingCart, TbClockHour4, TbTruckDelivery, TbCircleCheck, TbX, TbBan } from "react-icons/tb";

const ORDER_STATUSES = [
    'pending',
    'in_progress',
    'shipped',
    'delivered',
    'completed',
    'cancelByUser',
    'reject',
];

const getStatusLabel = (status) => {
    const labels = {
        pending: 'Pending',
        in_progress: 'In Progress',
        shipped: 'Shipped',
        delivered: 'Delivered',
        completed: 'Completed',
        cancelByUser: 'Cancelled By User',
        reject: 'Rejected',
    };

    return labels[status] || status;
};

const getStatusBadgeClass = (status) => {
    switch (status) {
        case 'pending': return 'bg-warning';
        case 'in_progress': return 'bg-info';
        case 'shipped': return 'bg-primary';
        case 'delivered':
        case 'completed': return 'bg-success';
        case 'cancelByUser':
        case 'reject': return 'bg-danger';
        default: return 'bg-secondary';
    }
};

const CorderList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const cartData = useSelector((state) => state.orderDB);
    const [showModal, setShowModal] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);

    useEffect(() => {
        dispatch(getOrderListDB());
    }, [dispatch]);

    const handleEditClick = (product) => {
        setCurrentProduct(product);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentProduct(null);
    };

    const handleSave = () => {
        dispatch(updateOrderListDB({
            id: currentProduct.id,
            data: { status: currentProduct.status || 'pending' }
        }));
        handleCloseModal();
    };

    const tableHeaders = [
        { Header: 'ID', accessor: 'id' },
        { Header: 'Name', accessor: 'customer.name', Cell: ({ row }) => row.original.customer?.name || row.original?.guest_name },
        { Header: 'Price', accessor: 'total_amount' },
        { Header: 'Payment', accessor: 'payment_method' },
        { Header: 'Date', accessor: 'createdAt' },
        {
            Header: 'Address',
            accessor: 'shipping_address',
            Cell: ({ row }) => <TruncatedMessage text={row.original.shipping_address || ''} maxLength={30} />
        },
        {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ row }) => {
                const status = row.original.status || '';
                return (
                    <span className={`badge ${getStatusBadgeClass(status)} text-white p-2`}>
                        {getStatusLabel(status)}
                    </span>
                );
            }
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            Cell: ({ row }) => (
                <>
                    <button className="btn editbtn mx-1" onClick={() => handleEditClick(row.original)}>Confirm</button>
                    <button className="btn editbtn mx-1" onClick={() => navigate(`/billing/details/${row.original.id}`)}>See</button>
                </>
            ),
        },
    ];

    // Compute order counts
    const orderStats = {
        pending: cartData.data?.orders?.filter(o => o.status === 'pending').length || 0,
        in_progress: cartData.data?.orders?.filter(o => o.status === 'in_progress').length || 0,
        shipped: cartData.data?.orders?.filter(o => o.status === 'shipped').length || 0,
        delivered: cartData.data?.orders?.filter(o => o.status === 'delivered' || o.status === 'completed').length || 0,
        cancelByUser: cartData.data?.orders?.filter(o => o.status === 'cancelByUser').length || 0,
        reject: cartData.data?.orders?.filter(o => o.status === 'reject').length || 0,
    };

    const statCards = [
        { key: 'pending', label: 'Pending Orders', value: orderStats.pending, icon: <TbShoppingCart size={28} />, color: 'bg-warning' },
        { key: 'in_progress', label: 'In Progress', value: orderStats.in_progress, icon: <TbClockHour4 size={28} />, color: 'bg-info' },
        { key: 'shipped', label: 'Shipped', value: orderStats.shipped, icon: <TbTruckDelivery size={28} />, color: 'bg-primary' },
        { key: 'delivered', label: 'Delivered', value: orderStats.delivered, icon: <TbCircleCheck size={28} />, color: 'bg-success' },
        { key: 'cancelByUser', label: 'Cancelled', value: orderStats.cancelByUser, icon: <TbBan size={28} />, color: 'bg-danger' },
        { key: 'reject', label: 'Rejected', value: orderStats.reject, icon: <TbX size={28} />, color: 'bg-danger' },
    ];

    return (
        <>
            <div className="container-fluid mt-5">
                <div className="mainheadig mx-4">
                    <h4 className="text-white font-weight-bold">Order List</h4>
                </div>

                {/* Analytics Section */}
                <div className="row mx-4 mb-4">
                    {statCards.map((card) => (
                        <div className="col-xl-4 col-md-6 mb-4" key={card.key}>
                            <div className="card shadow-sm border-0 rounded-lg h-100">
                                <div className="card-body d-flex align-items-center">
                                    <div
                                        className={`${card.color} text-white me-3 d-flex align-items-center justify-content-center`}
                                        style={{ width: "50px", height: "50px", borderRadius: "12px" }}
                                    >
                                        {card.icon}
                                    </div>
                                    <div className="flex-grow-1 text-right">
                                        <h6 className="text-muted mb-1">{card.label}</h6>
                                        <h5 className="fw-bold mb-0">{card.value}</h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {cartData.data?.orders?.length > 0 && (
                    <AdminTable tableHeaders={tableHeaders} tableData={cartData.data?.orders} />
                )}
            </div>

            {showModal && currentProduct && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="editModalLabel" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="editModalLabel">View Order</h5>
                                <button type="button" className="close" onClick={handleCloseModal} aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <div className="form-group">
                                        <label htmlFor="status">Action</label>
                                        <select
                                            className="form-control"
                                            value={currentProduct.status || 'pending'}
                                            onChange={(e) => setCurrentProduct({ ...currentProduct, status: e.target.value })}
                                        >
                                            {ORDER_STATUSES.map((status) => (
                                                <option key={status} value={status}>
                                                    {getStatusLabel(status)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="productName">Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="productName"
                                            value={currentProduct.customer?.name || currentProduct.guest_name || ''}
                                            readOnly
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="productPrice">Price</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="productPrice"
                                            value={currentProduct?.total_amount || ''}
                                            readOnly
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="productQuantity">Contact Number</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="productQuantity"
                                            value={currentProduct.phone || currentProduct.customer?.mobile || ''}
                                            readOnly
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="productDescription">Address</label>
                                        <textarea
                                            className="form-control"
                                            id="productDescription"
                                            value={currentProduct?.shipping_address || ''}
                                            readOnly
                                        />
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
                                <button type="button" className="btn btn-primary" onClick={handleSave}>Save changes</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CorderList;
